import { AppDispatch } from '@typings/redux';
import { FormikHelpers } from 'formik';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormatter, useNotification } from 'components/shared/hooks';
import { useEnvironment } from 'components/shared/hooks/useEnvironment/useEnvironment';
import {
  fetchPasswordSecurity,
  getPasswordSecurity,
  putPasswordSecurity,
  PasswordSecurityData,
} from 'store/passwordSecurity';
import { PasswordSecurity } from './PasswordSecurity';
import { PasswordSecurityFormValues } from './PasswordSecurity.types';

const toFormValues = (data: PasswordSecurityData): PasswordSecurityFormValues => ({
  petentLevel: data.petentLevel,
  applicantLevel: data.applicantLevel,
  petentAllowSkip: data.petentAllowSkip ? 'allow' : 'force',
  applicantAllowSkip: data.applicantAllowSkip ? 'allow' : 'force',
});

const toApiValues = (values: PasswordSecurityFormValues): PasswordSecurityData => ({
  petentLevel: values.petentLevel,
  applicantLevel: values.applicantLevel,
  petentAllowSkip: values.petentAllowSkip === 'allow',
  applicantAllowSkip: values.applicantAllowSkip === 'allow',
});

export const PasswordSecurityContainer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { origin } = useEnvironment();
  const { success } = useNotification();
  const { formatApiErrors } = useFormatter();
  const [submitSuccess, setSubmitSuccess] = React.useState(false);
  const [savedFormValues, setSavedFormValues] = React.useState<PasswordSecurityFormValues | null>(null);
  const [hasFetched, setHasFetched] = React.useState(false);
  const { data, loading } = useSelector(getPasswordSecurity);

  React.useEffect(() => {
    if (!origin) return;
    (async () => {
      await dispatch(fetchPasswordSecurity(origin));
      setHasFetched(true);
    })();
  }, [dispatch, origin]);

  const handleFormSubmit = async (
    values: PasswordSecurityFormValues,
    formik: FormikHelpers<PasswordSecurityFormValues>,
  ) => {
    const payload = toApiValues(values);
    const result = await dispatch(putPasswordSecurity(payload, origin || window.location.origin));

    if (result.error && result.payload?.response?.errors) {
      formik.setErrors({
        ...formatApiErrors(result.payload.response.errors),
      });
      formik.setSubmitting(false);
      return;
    }

    if (result.error) {
      formik.setSubmitting(false);
      return;
    }

    setSavedFormValues(values);
    setSubmitSuccess(true);
    success('OFFICIALS.PASSWORD_SECURITY.SUCCESS');
    formik.setSubmitting(false);
  };

  const initialValues = toFormValues(data);

  return (
    <PasswordSecurity
      initialValues={initialValues}
      loading={loading || !hasFetched}
      handleFormSubmit={handleFormSubmit}
      submitted={submitSuccess}
      savedFormValues={savedFormValues}
    />
  );
};
